// ---------------------------------------------------------------------------
// The dark green sidebar.
//
// It renders only the links this employee holds the permission for. Signing in
// as a cashier therefore produces a short menu - Point of Sale, Orders,
// Customers, My Shift - and nothing else, because a cashier holds none of the
// other permissions.
//
// It collapses to an icon rail to give the POS screen more room, and on a
// phone it becomes a slide-over panel.
// ---------------------------------------------------------------------------

import { NavLink } from 'react-router-dom';
import { useOnlineOrders } from '../../api/menu.api';
import { usePermission } from '../../hooks/usePermissions';
import {
  BarChart3,
  Beef,
  ChefHat,
  ChevronLeft,
  ChevronRight,
  Circle,
  ClipboardList,
  Clock,
  CloudOff,
  DatabaseBackup,
  Factory,
  LayoutDashboard,
  ListChecks,
  Package,
  Receipt,
  ScrollText,
  Settings,
  ShoppingCart,
  Tags,
  Tag,
  Zap,
  Globe,
  Trash2,
  Truck,
  Upload,
  UserCog,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../../stores/authStore';
import { useI18n } from '../../i18n/I18nProvider';
import { NAV_GROUPS, visibleNavItems, type NavItem } from '../../routes/navigation';
import { YaghiLogo } from '../common/YaghiLogo';
import { useOverlayDismiss } from '../common/overlayStack';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /// Mobile slide-over state.
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

// Only the icons the menu actually uses.
//
// This map replaced `import * as Icons from 'lucide-react'`. That version was
// convenient but pulled the entire icon library into the bundle - roughly
// 700 kB of icons for the seventeen this menu needs - which is exactly the
// kind of weight the POS screen cannot afford. Naming them explicitly lets the
// bundler drop the rest.
//
// Adding a menu entry: add its icon here too, or it falls back to a circle.
const NAV_ICONS: Record<string, LucideIcon> = {
  LayoutDashboard,
  ShoppingCart,
  ClipboardList,
  Users,
  Beef,
  Package,
  ListChecks,
  ChefHat,
  Trash2,
  Truck,
  Factory,
  Clock,
  Receipt,
  BarChart3,
  UserCog,
  ScrollText,
  Settings,
  // --- Phase 8 and 9 ---
  Upload,
  DatabaseBackup,
  CloudOff,
  Tags,
  Tag,
  Zap,
  Globe,
};

/// Falling back to a neutral shape means a mistyped icon name never breaks the
/// whole menu.
function NavIcon({ name, className }: { name: string; className?: string }) {
  const Component = NAV_ICONS[name] ?? Circle;
  return <Component className={className} />;
}

function NavRow({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const { t } = useI18n();
  const label = t.nav[item.labelKey];

  // HOW MANY ARE WAITING, for the one row where that is the whole point. The
  // SAME query the Menu Orders page and the arrival notice use - same key,
  // same cache entry, so this adds no second poll of the server. It simply
  // reads a number somebody else was already fetching.
  const mayReview = usePermission('menu.orders.view');
  const { data: pending } = useOnlineOrders('PENDING_CONFIRMATION', mayReview && item.path === '/menu-orders');
  const waiting = item.path === '/menu-orders' ? (pending?.counts?.pending ?? 0) : 0;

  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        clsx(
          'group relative flex items-center gap-3 rounded-control px-3 py-2.5 text-sm font-medium',
          'transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-antique-gold focus-visible:ring-offset-2 focus-visible:ring-offset-deep-green',
          collapsed && 'justify-center px-0',
          isActive
            ? 'bg-warm-cream/95 text-deep-green shadow-sm'
            : 'text-warm-cream/80 hover:bg-white/10 hover:text-warm-cream',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* A gold marker on the active row, so the current page is not
              signalled by colour alone. */}
          <span
            aria-hidden="true"
            className={clsx(
              'absolute inset-y-1.5 start-0 w-1 rounded-full bg-antique-gold transition-opacity',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
          />
          <NavIcon name={item.icon} className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="truncate">{label}</span>}

          {/* New menu orders, counted. On the collapsed rail it becomes a dot
              on the icon, because there is no room for a number and "there is
              something here" is the part that matters at a glance. */}
          {waiting > 0 ? (
            collapsed ? (
              <span
                aria-hidden="true"
                className="absolute end-2 top-2 h-2 w-2 rounded-full bg-burgundy-red"
                data-testid="menu-orders-badge-dot"
              />
            ) : (
              <span
                className="numeric ms-auto rounded-full bg-burgundy-red px-1.5 py-0.5 text-[11px] font-semibold text-warm-cream"
                data-testid="menu-orders-badge"
              >
                {waiting}
              </span>
            )
          ) : null}

          {/* Screens that are not built yet, and modules that are built but
              deliberately paused, carry the same small marker - from a
              cashier's seat the two are the same fact: it is not usable yet.
              Abbreviated on the badge so the rail keeps its width in both
              languages; the full sentence is on the page itself. */}
          {!collapsed && (item.phase || item.comingSoon) ? (
            <span
              className={clsx(
                'ms-auto rounded px-1.5 py-0.5 text-[10px] font-semibold',
                isActive ? 'bg-deep-green/10 text-deep-green' : 'bg-white/10 text-warm-cream/60',
              )}
            >
              {/* "Not built yet" for a screen that does not exist; "Coming
                  Soon" for a module that does and is paused. Different facts,
                  and a shop that has recipes in its database deserves the
                  second rather than the first. */}
              {item.comingSoon ? t.comingSoon.badge : t.common.comingSoon}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

export function Sidebar({ collapsed, onToggleCollapsed, mobileOpen, onCloseMobile }: SidebarProps) {
  // The slide-over menu is an overlay like any other: Escape closes it.
  useOverlayDismiss(mobileOpen, onCloseMobile);
  const permissions = useAuthStore((state) => state.permissions);
  const { t, dir } = useI18n();

  const items = visibleNavItems(permissions);
  const groupsWithItems = NAV_GROUPS.map((group) => ({
    ...group,
    items: items.filter((item) => item.group === group.key),
  })).filter((group) => group.items.length > 0);

  const CollapseIcon = dir === 'rtl' ? ChevronRight : ChevronLeft;
  const ExpandIcon = dir === 'rtl' ? ChevronLeft : ChevronRight;

  const content = (
    <>
      {/* --- brand ------------------------------------------------------- */}
      <div
        className={clsx(
          'flex h-16 shrink-0 items-center border-b border-white/10',
          collapsed ? 'justify-center px-2' : 'gap-3 px-4',
        )}
      >
        <YaghiLogo height={collapsed ? 28 : 34} variant="light" />
        {!collapsed && (
          <div className="min-w-0 leading-tight">
            <p className="truncate text-sm font-semibold text-warm-cream">Yaghi Butcher</p>
            <p className="truncate text-xs text-warm-cream/60">ملحمة ياغي</p>
          </div>
        )}
      </div>

      {/* --- links -------------------------------------------------------- */}
      <nav className="scrollbar-brand flex-1 space-y-5 overflow-y-auto px-2.5 py-4" aria-label="Main">
        {groupsWithItems.map((group) => (
          <div key={group.key}>
            {!collapsed && (
              <p className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-warm-cream/40">
                {t.nav[group.labelKey]}
              </p>
            )}
            {collapsed && <div className="mx-3 mb-2 border-t border-white/10" aria-hidden="true" />}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavRow key={item.path} item={item} collapsed={collapsed} onNavigate={onCloseMobile} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* --- collapse control --------------------------------------------- */}
      <div className="shrink-0 border-t border-white/10 p-2.5">
        <button
          type="button"
          onClick={onToggleCollapsed}
          className={clsx(
            'hidden w-full items-center gap-2 rounded-control px-3 py-2 text-sm text-warm-cream/70',
            'transition-colors hover:bg-white/10 hover:text-warm-cream lg:flex',
            collapsed && 'justify-center px-0',
          )}
          aria-label={collapsed ? t.nav.expand : t.nav.collapse}
        >
          {collapsed ? (
            <ExpandIcon className="h-5 w-5" aria-hidden="true" />
          ) : (
            <>
              <CollapseIcon className="h-5 w-5" aria-hidden="true" />
              <span>{t.nav.collapse}</span>
            </>
          )}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* --- desktop ------------------------------------------------------ */}
      <aside
        className={clsx(
          // `min-w-0` and `overflow-hidden` are belt and braces for the rail.
          // This aside is a flex item, and a flex item's default
          // `min-width: auto` is the minimum size of its CONTENT - which for
          // the expanded menu is about 256 px. Nothing here relies on that
          // default staying harmless: the two classes below switch it off, so
          // `w-[72px]` decides the width rather than the longest menu label.
          'z-30 hidden min-w-0 shrink-0 flex-col overflow-hidden bg-deep-green shadow-sidebar transition-[width] duration-150 lg:flex',
          collapsed ? 'w-[72px]' : 'w-64',
        )}
      >
        {content}
      </aside>

      {/* --- mobile slide-over -------------------------------------------- */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-charcoal/50"
            onClick={onCloseMobile}
            aria-hidden="true"
          />
          <aside
            className="absolute inset-y-0 start-0 flex w-72 flex-col bg-deep-green shadow-sidebar"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <button
              type="button"
              onClick={onCloseMobile}
              className="absolute end-3 top-4 rounded-control p-1.5 text-warm-cream/70 hover:bg-white/10"
              aria-label={t.common.close}
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
            {content}
          </aside>
        </div>
      )}
    </>
  );
}
